import React, { ReactNode, useState } from "react";
import { NavLink, useLocation, useNavigate, useParams } from "react-router-dom";
import { appRoutes, normalizeAppPathname } from "../../routes/appRoutes";
import IconFont from "./IconFont";
import PageNavigatorInput from "./PageNavigatorInput";

interface ContextHeaderProps {
  titleIsEditable?: boolean;
  headingTitle: string;
  EditableTitleComponent?: ReactNode | null;
  linkTo?: string;
  className?: string;
  children?: ReactNode;
  statusComponent?: ReactNode;
}

/**
 * PageNavigator component
 *
 * This component renders a header with customizable options. Note that this component is not fully reusable yet; consider refactoring it for reusability in the future.
 *
 * @param {ContextHeaderProps} props - The props for the PageNavigator component.
 * @param {boolean} props.titleIsEditable - (required) Whether the title can be edited or is static.
 * @param {string} props.headingTitle - (required) The text of the static title or the initial placeholder of the title.
 * @param {string} props.linkTo - (optional) The link for the back button or the title if `titleIsEditable` is false. defaults back
 * @param {string} [props.className] - (optional) The className for the PageNavigator component.
 * @param {ReactNode} [props.children] - (optional) children example: the 3 dot menu or the element on the right side
 * @param {ReactNode} [props.EditableTitleComponent] - (optional) - add your own editable title component if you have a special way of handling your editable title - default <PageNavigatorInput /> - import component <PageNavigatorInput /> for an easier time.
 * 
 * @returns JSX.Element - The `PageNavigator` component.
 *
 * @note This component is not fully reusable; consider converting it to a reusable component in the future.
 */
const PageNavigator: React.FC<ContextHeaderProps> = ({ titleIsEditable = false, EditableTitleComponent = null, headingTitle, linkTo = "..", className = "", children, statusComponent }) => {
  const [editableTitleValue, setEditableTitleValue] = useState<string>(headingTitle)
  const location = useLocation();
  const params = useParams();
  const navigate = useNavigate();
  // const { sendMessage } = useSendMessage();

  const handleChange = (value: string) => {
    setEditableTitleValue(value)
  }

  const removeElementInspector = async () => {
    // const response = await sendMessage({ command: "removeInspector", data: "" })
    // console.log(response)
  }

  const variantPath = params.experimentId && params.variantId
    ? appRoutes.experiments.variant(params.experimentId, params.variantId)
    : null;
  const isChangeEditor = Boolean(
    variantPath && normalizeAppPathname(location.pathname) !== variantPath,
  );

  const handleBack = () => {
    if (params.experimentId && params.variantId) {
      navigate(
        isChangeEditor
          ? appRoutes.experiments.variant(params.experimentId, params.variantId)
          : appRoutes.experiments.setup(params.experimentId),
        { viewTransition: true },
      );
      return;
    }

    if (params.experimentId) {
      navigate(appRoutes.experiments.root, { viewTransition: true });
      return;
    }

    history.back();
  }

  return (
    <div className={className + " ui-page-navigator relative z-10 translate-x-[-28px]"}>
      <div className="ui-page-header-row px-7 border-b border-b-border-default bg-background-surface-01">
          <div className={`${titleIsEditable ? "flex" : "hidden"} min-w-0 flex-1 items-center gap-[4px]`}>
            {linkTo === ".." ? (
              <button type="button" className="btn-icon h-8 w-4 p-0" onClick={() => handleBack()}>
                <IconFont name="chevron-left" size={24} />
              </button>
            ) : (
              <NavLink to={linkTo} className="btn-icon h-8 w-4 p-0 inline-flex items-center justify-center">
                <IconFont name="chevron-left" size={24} />
              </NavLink>
            )}
            <div className="min-w-0 flex-1">
              {EditableTitleComponent ? EditableTitleComponent : <PageNavigatorInput value={editableTitleValue} onValueChange={handleChange} />}
            </div>
          </div>
          {linkTo === ".." ? <div className={`${titleIsEditable ? "hidden" : "flex"} min-w-0 flex-1 items-center gap-[4px]`}>
            <button type="button" className="btn-icon h-8 w-4 p-0" onClick={() => {
            if (isChangeEditor) {
              removeElementInspector()
            }
            handleBack()
          }}>
              <IconFont name="chevron-left" size={24} />
            </button>
            <span className="h-6 min-w-0 truncate type-heading-lg leading-6">{headingTitle}</span>
          </div> : <div className={`${titleIsEditable ? "hidden" : "flex"} min-w-0 flex-1 items-center gap-[4px]`}>
            <NavLink to={linkTo} className="btn-icon h-8 w-4 p-0 inline-flex items-center justify-center">
              <IconFont name="chevron-left" size={24} />
            </NavLink>
            <span className="h-6 min-w-0 truncate type-heading-md leading-6">{headingTitle}</span>
          </div>
          }

          {statusComponent}
          <div className="ml-auto flex h-full items-center">
            {children}
          </div>
      </div>
    </div>
  );
};

export default PageNavigator;
